import type { ParamsGetInputsProductsAll } from "@CommonApi/inputs/products/all";
import type { FilterModel } from "ag-grid-community";
import { normalizeMultiValue } from "src/merchflows/shared/multiValue";

export type ProductBackendTextFilterModel = {
  filterType: "backendTextInput";
  value: string;
};

export type ProductMultiSelectFilterModel = {
  filterType: "multiSelect";
  values: string[];
};

type ProductFilterQueryParams = Pick<
  ParamsGetInputsProductsAll,
  "upc" | "product_code" | "name" | "brand" | "category" | "subcategory"
>;

const getTextFilterModel = (model: unknown): ProductBackendTextFilterModel | null => {
  if (!model || typeof model !== "object") {
    return null;
  }

  const textModel = model as ProductBackendTextFilterModel;
  if (textModel.filterType === "backendTextInput") {
    return textModel;
  }

  return null;
};

const getMultiSelectFilterModel = (model: unknown): ProductMultiSelectFilterModel | null => {
  if (!model || typeof model !== "object") {
    return null;
  }

  const multiSelectModel = model as ProductMultiSelectFilterModel;
  if (multiSelectModel.filterType === "multiSelect" && Array.isArray(multiSelectModel.values)) {
    return multiSelectModel;
  }

  return null;
};

const readExactText = (filterModel: FilterModel, columnId: keyof ProductFilterQueryParams) => {
  const model = getTextFilterModel(filterModel[columnId]);

  if (!model || typeof model.value !== "string" || model.value === "") {
    return undefined;
  }

  return model.value;
};

const readMultiSelectValues = <T extends string = string>(
  filterModel: FilterModel,
  columnId: keyof ProductFilterQueryParams,
): T[] | undefined => {
  const model = getMultiSelectFilterModel(filterModel[columnId]);

  if (!model) {
    return undefined;
  }

  const values = model.values
    .filter((value): value is T => typeof value === "string" && value !== "")
    .filter((value, index, array) => array.indexOf(value) === index);

  return values.length > 0 ? values : undefined;
};

const toTextFilterModel = (
  value: string | undefined,
): ProductBackendTextFilterModel | undefined => {
  if (value === undefined || value === "") {
    return undefined;
  }

  return {
    filterType: "backendTextInput",
    value,
  };
};

const toMultiSelectFilterModel = (
  values: string | readonly string[] | undefined,
): ProductMultiSelectFilterModel | undefined => {
  const normalizedValues = normalizeMultiValue(values);

  if (!normalizedValues?.length) {
    return undefined;
  }

  return {
    filterType: "multiSelect",
    values: [...normalizedValues],
  };
};

export const gridFilterModelToQueryParams = (
  filterModel: FilterModel,
): ProductFilterQueryParams => {
  return {
    upc: readExactText(filterModel, "upc"),
    product_code: readExactText(filterModel, "product_code"),
    name: readExactText(filterModel, "name"),
    brand: readExactText(filterModel, "brand"),
    category: readMultiSelectValues(filterModel, "category"),
    subcategory: readMultiSelectValues(filterModel, "subcategory"),
  };
};

export const queryParamsToGridFilterModel = (
  queryParams: ProductFilterQueryParams,
): FilterModel => {
  const filterModel: FilterModel = {};

  for (const key of Object.keys(queryParams) as (keyof ProductFilterQueryParams)[]) {
    if (key === "category" || key === "subcategory") {
      const multiSelectFilterModel = toMultiSelectFilterModel(queryParams[key]);

      if (multiSelectFilterModel) {
        filterModel[key] = multiSelectFilterModel;
      }
      continue;
    }

    const textFilterModel = toTextFilterModel(
      typeof queryParams[key] === "string" ? queryParams[key] : undefined,
    );

    if (textFilterModel) {
      filterModel[key] = textFilterModel;
    }
  }

  return filterModel;
};
